import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const TaxCalculator = () => {
  const [income, setIncome] = useState('');
  const [age, setAge] = useState('');
  const [deductions, setDeductions] = useState('');
  const [taxableIncome, setTaxableIncome] = useState(0);
  const [tax, setTax] = useState(0);
  const [effectiveRate, setEffectiveRate] = useState(0);
  const [takeHome, setTakeHome] = useState(0);

  const calculateTax = () => {
    const grossIncome = parseFloat(income) || 0;
    const totalDeductions = parseFloat(deductions) || 0;
    const taxableIncomeValue = Math.max(0, grossIncome - totalDeductions);
    
    setTaxableIncome(taxableIncomeValue);

    let taxValue = 0;
    const ageValue = parseInt(age) || 0;
    
    // Tax slabs for FY 2023-24
    if (taxableIncomeValue <= 250000) {
      taxValue = 0;
    } else if (taxableIncomeValue <= 500000) {
      if (ageValue < 60) {
        taxValue = (taxableIncomeValue - 250000) * 0.05;
      } else {
        taxValue = 0;
      }
    } else if (taxableIncomeValue <= 750000) {
      if (ageValue < 60) {
        taxValue = 12500 + (taxableIncomeValue - 500000) * 0.10;
      } else if (ageValue < 80) {
        taxValue = (taxableIncomeValue - 300000) * 0.05;
      } else {
        taxValue = 0;
      }
    } else if (taxableIncomeValue <= 1000000) {
      if (ageValue < 60) {
        taxValue = 37500 + (taxableIncomeValue - 750000) * 0.15;
      } else if (ageValue < 80) {
        taxValue = 22500 + (taxableIncomeValue - 500000) * 0.10;
      } else {
        taxValue = (taxableIncomeValue - 500000) * 0.10;
      }
    } else if (taxableIncomeValue <= 1250000) {
      if (ageValue < 60) {
        taxValue = 75000 + (taxableIncomeValue - 1000000) * 0.20;
      } else if (ageValue < 80) {
        taxValue = 47500 + (taxableIncomeValue - 750000) * 0.15;
      } else {
        taxValue = 50000 + (taxableIncomeValue - 500000) * 0.20;
      }
    } else if (taxableIncomeValue <= 1500000) {
      if (ageValue < 60) {
        taxValue = 125000 + (taxableIncomeValue - 1250000) * 0.25;
      } else if (ageValue < 80) {
        taxValue = 85000 + (taxableIncomeValue - 1000000) * 0.20;
      } else {
        taxValue = 150000 + (taxableIncomeValue - 1000000) * 0.20;
      }
    } else {
      if (ageValue < 60) {
        taxValue = 187500 + (taxableIncomeValue - 1500000) * 0.30;
      } else if (ageValue < 80) {
        taxValue = 125000 + (taxableIncomeValue - 1250000) * 0.25;
      } else {
        taxValue = 250000 + (taxableIncomeValue - 1250000) * 0.25;
      }
    }

    // Add 4% cess
    taxValue = taxValue * 1.04;
    
    setTax(Math.round(taxValue));
    setEffectiveRate(grossIncome > 0 ? (taxValue / grossIncome) * 100 : 0);
    setTakeHome(grossIncome - taxValue);
  };

  useEffect(() => {
    calculateTax();
  }, [income, age, deductions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const copyResult = () => {
    const resultText = `Income Tax Calculation:
Gross Income: ${formatCurrency(parseFloat(income) || 0)}
Age: ${age} years
Deductions: ${formatCurrency(parseFloat(deductions) || 0)}
Taxable Income: ${formatCurrency(taxableIncome)}
Tax Payable: ${formatCurrency(tax)}
Effective Rate: ${effectiveRate.toFixed(2)}%
Take Home: ${formatCurrency(takeHome)}`;
    
    navigator.clipboard.writeText(resultText);
    alert('Results copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Tax Calculator – Maximize Tax Savings"
        description="Calculate your income tax and maximize tax savings with our comprehensive tax calculator. Get instant tax calculations, effective tax rate, and tax saving tips for India."
        keywords="tax calculator, tax saving tips, income tax calculation, tax planning, tax deductions, income tax India, tax calculator 2025"
        type="webapplication"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Calculators', url: '/calculators' },
          { name: 'Tax Calculator', url: '/calculators/tax' }
        ]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Tax Calculator',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR'
          },
          description: 'Free online income tax calculator for calculating tax liability and maximizing tax savings'
        }}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Tax Calculator', url: '/calculators/tax' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Income Tax Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calculate your income tax and discover ways to maximize your tax savings
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Income Details
            </h2>
            
            <div className="space-y-6">
              {/* Gross Income */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gross Annual Income
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    placeholder="Enter your annual income"
                    className="input-field pl-8"
                  />
                </div>
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <select
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select your age</option>
                  <option value="25">Below 60 years</option>
                  <option value="65">60-80 years (Senior Citizen)</option>
                  <option value="85">Above 80 years (Super Senior Citizen)</option>
                </select>
              </div>

              {/* Deductions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Deductions (80C, 80D, etc.)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    value={deductions}
                    onChange={(e) => setDeductions(e.target.value)}
                    placeholder="Enter total deductions"
                    className="input-field pl-8"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Include 80C (1.5L), 80D, HRA, LTA, etc.
                </p>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {/* Tax Result */}
            <div className="result-card">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Tax Payable</h3>
                <div className="text-4xl font-bold text-red-600 mb-2">
                  {formatCurrency(tax)}
                </div>
                <p className="text-sm text-gray-600">
                  Effective Rate: {effectiveRate.toFixed(2)}%
                </p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Tax Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Gross Income</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(parseFloat(income) || 0)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Deductions</span>
                  <span className="font-semibold text-green-600">
                    -{formatCurrency(parseFloat(deductions) || 0)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Taxable Income</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(taxableIncome)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-semibold">Take Home</span>
                  <span className="font-bold text-primary-600 text-lg">
                    {formatCurrency(takeHome)}
                  </span>
                </div>
              </div>

              <button
                onClick={copyResult}
                className="w-full mt-6 btn-primary"
              >
                Copy Results
              </button>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 Tax Saving Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Maximize 80C deductions (1.5L limit)</li>
              <li>• Invest in ELSS mutual funds</li>
              <li>• Claim HRA and LTA benefits</li>
              <li>• Consider NPS for additional deduction</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📊 Tax Slabs 2023-24
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div>• 0-2.5L: No tax</div>
              <div>• 2.5-5L: 5%</div>
              <div>• 5-7.5L: 10%</div>
              <div>• 7.5-10L: 15%</div>
              <div>• 10-12.5L: 20%</div>
              <div>• 12.5-15L: 25%</div>
              <div>• Above 15L: 30%</div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🏦 Popular Deductions
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• 80C: Up to ₹1.5L (PPF, ELSS, etc.)</li>
              <li>• 80D: Health insurance premium</li>
              <li>• 80E: Education loan interest</li>
              <li>• 80G: Donations to charity</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TaxCalculator;
